import React, { useRef, useState } from 'react';

import {
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
} from '@chakra-ui/react';
import { Field, FieldProps } from 'formik';
import { HiOutlinePaperClip } from 'react-icons/hi2';

import { CustomText } from '@components/Custom-text';

import UploadedFiles from './UploadedFiles';

interface FormControlMultiFilesFieldProps {
    name: string;
    label: string;
    placeholder: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    selectedFiles?: string[];
    setFileIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const FormControlMultiFilesField: React.FC<FormControlMultiFilesFieldProps> = ({
    name,
    label,
    placeholder,
    isDisabled,
    isRequired,
    selectedFiles,
    setFileIds,
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>([]);

    return (
        <Field name={name}>
            {({ field, form: { touched, errors, setFieldValue } }: FieldProps) => {
                const isInvalid = !!errors[field.name] && touched[field.name];
                return (
                    <FormControl isInvalid={Boolean(isInvalid)}>
                        <Flex>
                            <FormLabel>
                                <Heading color="titleText" size="xs" as="h6">
                                    {label}
                                </Heading>
                            </FormLabel>
                            {isRequired ? (
                                <Heading color="red" size="xs" ml={-2}>
                                    *
                                </Heading>
                            ) : null}
                        </Flex>
                        <UploadedFiles
                            name={name}
                            files={files}
                            setFiles={setFiles}
                            selectedFiles={selectedFiles}
                            setFieldValue={setFieldValue}
                            setFileIds={setFileIds}
                        />
                        <InputGroup width="100%">
                            <Input
                                id={name}
                                name={name}
                                ref={inputRef}
                                type="file"
                                disabled={isDisabled}
                                style={{ display: 'none' }}
                                accept=".pdf,.docx"
                                data-testid="fileInput"
                                onChange={e => {
                                    const fileInput = e.target;
                                    const file = fileInput.files ? fileInput.files[0] : null;
                                    if (file) {
                                        setFiles(prevFiles => [...prevFiles, file]);
                                        setFieldValue(name, [...files, file]);
                                        fileInput.value = '';
                                    }
                                }}
                            />
                            <FormLabel htmlFor={name} width="100%" margin={0}>
                                <Flex
                                    width="100%"
                                    height="2.5rem"
                                    border="none"
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    borderColor={isInvalid ? 'red.500' : 'inherit'}
                                    borderStyle="solid"
                                    cursor="pointer"
                                    borderRadius="0.375rem"
                                >
                                    <Icon
                                        as={HiOutlinePaperClip}
                                        color="primary.dark"
                                        fontSize={20}
                                        cursor="pointer"
                                    />
                                    <CustomText color="mainText" size="md" dataTestid="fileOutput">
                                        {placeholder}
                                    </CustomText>
                                </Flex>
                            </FormLabel>
                        </InputGroup>
                        <FormErrorMessage>
                            <CustomText size="xs" color="red.500">
                                {String(errors[field.name])}
                            </CustomText>
                        </FormErrorMessage>
                    </FormControl>
                );
            }}
        </Field>
    );
};

export default FormControlMultiFilesField;
